import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { faCamera, faCheckCircle, faCog, faRedo } from '@fortawesome/free-solid-svg-icons';
import { Property } from '@app/_models/propery.model';
import { PropertyService } from '@app/_services/property.service';

export type FieldMode = 'required' | 'optional' | 'off';

export type RegistrationFieldKey =
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'dob'
  | 'trn'
  | 'registrantType'
  | 'picture';

export interface KioskRegistrationConfig {
  propertyId: number | null;
  propertyName: string;
  fields: Record<RegistrationFieldKey, FieldMode>;
}

export const KIOSK_REGISTRATION_STORAGE_KEY = 'kioskRegistrationConfig';

const FIELD_MODES: FieldMode[] = ['required', 'optional', 'off'];

@Component({
  selector: 'app-kiosk-access-registration',
  templateUrl: './kiosk-access-registration.component.html',
  styleUrls: ['./kiosk-access-registration.component.css'],
  standalone: false
})
export class KioskAccessRegistrationComponent implements OnInit, OnDestroy {
  @ViewChild('cameraVideo') cameraVideo?: ElementRef<HTMLVideoElement>;

  private readonly successResetMs = 5000;

  faCamera = faCamera;
  faCheckCircle = faCheckCircle;
  faCog = faCog;
  faRedo = faRedo;

  readonly fieldDefinitions: { key: RegistrationFieldKey; label: string }[] = [
    { key: 'firstName', label: 'First Name' },
    { key: 'middleName', label: 'Middle Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'dob', label: 'D.O.B' },
    { key: 'trn', label: 'T.R.N' },
    { key: 'registrantType', label: 'Company/Individual' },
    { key: 'picture', label: 'Picture' }
  ];
  readonly fieldModes = FIELD_MODES;
  readonly maxDob = new Date().toISOString().slice(0, 10);

  config: KioskRegistrationConfig | null = null;
  properties: Property[] = [];
  setupForm: FormGroup;
  registrationForm: FormGroup | null = null;

  submitted = false;
  showSuccess = false;
  cameraActive = false;
  cameraError = '';

  currentTimeLabel = '';
  currentDateLabel = '';
  weatherLabel = 'Loading weather...';

  private mediaStream: MediaStream | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private weatherTimer: ReturnType<typeof setInterval> | null = null;
  private successTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly propertyService: PropertyService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.setupForm = this.fb.group({
      propertyName: ['', [Validators.required, Validators.maxLength(100)]],
      fields: this.fb.group(
        this.fieldDefinitions.reduce((group, field) => {
          group[field.key] = ['optional'];
          return group;
        }, {} as Record<string, unknown>)
      )
    });
  }

  ngOnInit(): void {
    this.config = this.loadConfig();

    if (this.config) {
      this.startKiosk();
    } else {
      this.prepareSetup();
    }
  }

  ngOnDestroy(): void {
    this.stopKioskTimers();
    this.stopCamera();
  }

  get fieldsGroup(): FormGroup {
    return this.setupForm.get('fields') as FormGroup;
  }

  get hasEnabledField(): boolean {
    return Object.values(this.fieldsGroup.value).some((mode) => mode !== 'off');
  }

  isEnabled(key: RegistrationFieldKey): boolean {
    return !!this.config && this.config.fields[key] !== 'off';
  }

  isRequired(key: RegistrationFieldKey): boolean {
    return !!this.config && this.config.fields[key] === 'required';
  }

  showError(key: RegistrationFieldKey): boolean {
    const control = this.registrationForm?.get(key);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  // ---------- Setup ----------

  private prepareSetup(): void {
    this.propertyService.getAll().subscribe({
      next: (properties) => (this.properties = properties ?? []),
      error: () => (this.properties = [])
    });
  }

  saveSetup(): void {
    this.setupForm.markAllAsTouched();
    if (this.setupForm.invalid || !this.hasEnabledField) {
      return;
    }

    const propertyName = String(this.setupForm.value.propertyName).trim();
    const match = this.properties.find((p) => p.name === propertyName);

    const config: KioskRegistrationConfig = {
      propertyId: match?.id ?? null,
      propertyName,
      fields: { ...this.fieldsGroup.value }
    };

    localStorage.setItem(KIOSK_REGISTRATION_STORAGE_KEY, JSON.stringify(config));
    this.config = config;
    this.startKiosk();
  }

  reconfigure(): void {
    if (!confirm('Reconfigure this kiosk? The current setup will be cleared.')) {
      return;
    }

    this.stopKioskTimers();
    this.stopCamera();

    if (this.config) {
      this.setupForm.patchValue({ propertyName: this.config.propertyName, fields: this.config.fields });
    }

    localStorage.removeItem(KIOSK_REGISTRATION_STORAGE_KEY);
    this.config = null;
    this.registrationForm = null;
    this.prepareSetup();
  }

  private loadConfig(): KioskRegistrationConfig | null {
    try {
      const raw = localStorage.getItem(KIOSK_REGISTRATION_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as KioskRegistrationConfig;
      const validFields = this.fieldDefinitions.every((f) => FIELD_MODES.includes(parsed?.fields?.[f.key]));

      if (typeof parsed?.propertyName !== 'string' || !parsed.propertyName.trim() || !validFields) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  // ---------- Kiosk registration ----------

  private startKiosk(): void {
    this.buildRegistrationForm();

    this.updateClock();
    this.clockTimer = setInterval(() => this.updateClock(), 1000);

    this.loadLocalWeather();
    this.weatherTimer = setInterval(() => this.loadLocalWeather(), 900000);
  }

  private buildRegistrationForm(): void {
    if (!this.config) {
      return;
    }

    const group: Record<string, unknown> = {};
    const nameValidators = [Validators.maxLength(50), Validators.pattern(/^[A-Za-z][A-Za-z '.-]*$/)];

    for (const { key } of this.fieldDefinitions) {
      if (!this.isEnabled(key)) {
        continue;
      }

      const validators = this.isRequired(key) ? [Validators.required] : [];

      switch (key) {
        case 'firstName':
        case 'middleName':
        case 'lastName':
          validators.push(...nameValidators);
          break;
        case 'dob':
          validators.push(this.pastDateValidator);
          break;
        case 'trn':
          validators.push(Validators.pattern(/^\d{3}-?\d{3}-?\d{3}$/));
          break;
      }

      group[key] = ['', validators];
    }

    this.registrationForm = this.fb.group(group);
    this.submitted = false;
  }

  private pastDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const date = new Date(control.value);
    return isNaN(date.getTime()) || date > new Date() ? { invalidDate: true } : null;
  }

  submitRegistration(): void {
    if (!this.registrationForm || !this.config) {
      return;
    }

    this.submitted = true;
    this.registrationForm.markAllAsTouched();

    if (this.registrationForm.invalid) {
      return;
    }

    // No registration endpoint exists yet; wire the payload to a service here.
    this.stopCamera();
    this.showSuccess = true;
    this.successTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.resetRegistration();
        this.cdr.markForCheck();
      });
    }, this.successResetMs);
  }

  resetRegistration(): void {
    if (this.successTimer) {
      clearTimeout(this.successTimer);
      this.successTimer = null;
    }

    this.showSuccess = false;
    this.stopCamera();
    this.buildRegistrationForm();
  }

  // ---------- Camera ----------

  async startCamera(): Promise<void> {
    this.cameraError = '';

    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError = 'Camera is not supported on this device.';
      return;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      });
      this.cameraActive = true;
      this.cdr.detectChanges();

      const video = this.cameraVideo?.nativeElement;
      if (video) {
        video.srcObject = this.mediaStream;
        await video.play();
      }
    } catch {
      this.cameraError = 'Unable to access the camera. Please allow camera permissions.';
      this.stopCamera();
    }
  }

  capturePhoto(): void {
    const video = this.cameraVideo?.nativeElement;
    if (!video || !video.videoWidth) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);

    const control = this.registrationForm?.get('picture');
    control?.setValue(canvas.toDataURL('image/jpeg', 0.85));
    control?.markAsTouched();
    this.stopCamera();
  }

  retakePhoto(): void {
    this.registrationForm?.get('picture')?.setValue('');
    this.startCamera();
  }

  stopCamera(): void {
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
    this.cameraActive = false;
  }

  // ---------- Clock / weather ----------

  private stopKioskTimers(): void {
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }

    if (this.weatherTimer) {
      clearInterval(this.weatherTimer);
      this.weatherTimer = null;
    }

    if (this.successTimer) {
      clearTimeout(this.successTimer);
      this.successTimer = null;
    }
  }

  private updateClock(): void {
    const now = new Date();
    this.currentTimeLabel = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    this.currentDateLabel = now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }

  private loadLocalWeather(): void {
    if (!('geolocation' in navigator)) {
      this.weatherLabel = 'Weather unavailable';
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const endpoint =
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}` +
          `&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=celsius`;

        try {
          const response = await fetch(endpoint);
          const data = response.ok ? await response.json() : null;
          const temperature = Math.round(Number(data?.current?.temperature_2m));
          const weatherCode = Number(data?.current?.weather_code);

          this.weatherLabel =
            Number.isFinite(temperature) && Number.isFinite(weatherCode)
              ? `${this.getWeatherText(weatherCode)} ${temperature}°C`
              : 'Weather unavailable';
        } catch {
          this.weatherLabel = 'Weather unavailable';
        }
        this.cdr.markForCheck();
      },
      () => (this.weatherLabel = 'Weather unavailable'),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  private getWeatherText(code: number): string {
    if (code === 0) return 'Clear';
    if ([1, 2, 3].includes(code)) return 'Cloudy';
    if ([45, 48].includes(code)) return 'Fog';
    if ([51, 53, 55, 56, 57].includes(code)) return 'Drizzle';
    if ([61, 63, 65, 66, 67].includes(code)) return 'Rain';
    if ([71, 73, 75, 77].includes(code)) return 'Snow';
    if ([80, 81, 82].includes(code)) return 'Rain showers';
    if ([85, 86].includes(code)) return 'Snow showers';
    if ([95, 96, 99].includes(code)) return 'Thunderstorm';
    return 'Weather';
  }
}
